import * as React from "react";
import { Link, navigate } from "gatsby";
import { useStickyAutoHideHeader } from "../hooks/useHeader";
import { maxWidth } from "../constants";
import * as styles from "../styles/header.module.scss";
import { StaticImage } from "gatsby-plugin-image";
import { useSiteMetadata } from "../hooks/useSiteMetadata";
import { useLocation } from "@reach/router";

function Header() {
  const { siteName } = useSiteMetadata();
  const { isVisible, isAtTop } = useStickyAutoHideHeader({
    hideAfter: 900,
    topOffset: 6,
  });

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    setIsMenuOpen(false);
  }, []);

  React.useEffect(() => {
    if (!isMenuOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsMenuOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isMenuOpen]);

  React.useEffect(() => {
    if (typeof document === "undefined") return;

    if (isMenuOpen) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }
  }, [isMenuOpen]);

  const handleMobileLink = (e, to) => {
    e.preventDefault();
    setIsMenuOpen(false);

    const [path, hash] = to.split("#");
    const targetPath = path || "/";
    const isCurrentPage =
      location.pathname === targetPath ||
      location.pathname === `${targetPath}/`;

    if (isCurrentPage && !hash) {
      // Same page, no hash — scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (isCurrentPage && hash) {
      // Same page with hash — scroll to section
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      // Different page — navigate
      navigate(to);
    }
  };

  return (
    <header
      className={`${styles.header} ${isAtTop ? styles.atTop : styles.scrolled}`}
      style={{ transform: isVisible ? "translateY(0)" : "translateY(-110%)" }}
    >
      <div style={{ width: "100%", maxWidth, margin: "0 auto" }}>
        <div className={styles.headerContainer}>
          <div className={styles.logoContainer}>
            <Link
              to="/"
              onClick={() => {
                window.gtag?.("event", "select_content", {
                  content_type: "navigation",
                  content_id: "home_logo",
                  menu_type: "header",
                  destination: "/",
                  page_location: window.location.pathname,
                });
              }}
            >
              <StaticImage
                src="../../src/assets/logos/remax-logo.png"
                alt={siteName}
              />
            </Link>
          </div>

          <nav className={styles.nav}>
            <Link
              to="/about-us"
              className={styles.navLink}
              activeClassName={styles.active}
              onClick={() => {
                window.gtag?.("event", "select_content", {
                  content_type: "navigation",
                  content_id: "about_us",
                  menu_type: "header",
                  destination: "/about-us",
                  page_location: window.location.pathname,
                });
              }}
            >
              About Us
            </Link>
            <Link
              to="/agents"
              className={styles.navLink}
              activeClassName={styles.active}
              onClick={() => {
                window.gtag?.("event", "select_content", {
                  content_type: "navigation",
                  content_id: "agents",
                  menu_type: "header",
                  destination: "/agents",
                  page_location: window.location.pathname,
                });
              }}
            >
              Agents
            </Link>
            <Link
              to="/#locations"
              className={styles.navLink}
              activeClassName={styles.active}
              onClick={() => {
                window.gtag?.("event", "select_content", {
                  content_type: "navigation",
                  content_id: "locations",
                  menu_type: "header",
                  destination: "/#locations",
                  page_location: window.location.pathname,
                });
              }}
            >
              Locations
            </Link>
            <Link
              to="/faqs"
              className={styles.navLink}
              activeClassName={styles.active}
              onClick={() => {
                window.gtag?.("event", "select_content", {
                  content_type: "navigation",
                  content_id: "faqs",
                  menu_type: "header",
                  destination: "/faqs",
                  page_location: window.location.pathname,
                });
              }}
            >
              FAQs
            </Link>
            <Link
              to="/contact-us"
              className={styles.navLink}
              activeClassName={styles.active}
              onClick={() => {
                window.gtag?.("event", "select_content", {
                  content_type: "navigation",
                  content_id: "contact_us",
                  menu_type: "header",
                  destination: "/contact-us",
                  page_location: window.location.pathname,
                });
              }}
            >
              Contact Us
            </Link>
          </nav>

          <button
            type="button"
            className={`${styles.menuButton} ${isAtTop ? styles.menuButtonAtTop : styles.menuButtonScrolled}`}
            onClick={() => {
              window.gtag?.("event", "select_content", {
                content_type: "navigation",
                content_id: "open_menu",
                menu_type: "header",
                destination: "mobile_menu",
                page_location: window.location.pathname,
              });
              setIsMenuOpen(true);
            }}
            aria-label="Open menu"
            aria-haspopup="dialog"
            aria-controls="mobile-menu"
            aria-expanded={isMenuOpen}
          >
            <span className={styles.menuIcon} aria-hidden="true" />
          </button>
        </div>
      </div>

      {isMenuOpen ? (
        <div
          id="mobile-menu"
          className={styles.menuOverlay}
          role="dialog"
          aria-modal="true"
          aria-label="Site menu"
        >
          <div className={styles.menuOverlayTop}>
            <div className={styles.menuOverlayBrand}>
              <StaticImage
                src="../../src/assets/logos/remax-logo.png"
                alt={siteName}
              />
            </div>
            <button
              type="button"
              className={styles.menuCloseButton}
              onClick={() => {
                window.gtag?.("event", "select_content", {
                  content_type: "navigation",
                  content_id: "close_menu",
                  menu_type: "header",
                  destination: "mobile_menu",
                  page_location: window.location.pathname,
                });
                setIsMenuOpen(false);
              }}
              aria-label="Close menu"
            >
              ✕
            </button>
          </div>

          <div className={styles.menuOverlayLinks}>
            <a
              href="/"
              className={styles.menuLink}
              onClick={(e) => {
                window.gtag?.("event", "select_content", {
                  content_type: "navigation",
                  content_id: "home",
                  menu_type: "header",
                  destination: "/",
                  page_location: window.location.pathname,
                });

                handleMobileLink(e, "/");
              }}
            >
              Home
            </a>
            <a
              href="/about-us"
              className={styles.menuLink}
              onClick={(e) => {
                window.gtag?.("event", "select_content", {
                  content_type: "navigation",
                  content_id: "about_us",
                  menu_type: "header",
                  destination: "/about-us",
                  page_location: window.location.pathname,
                });
                handleMobileLink(e, "/about-us");
              }}
            >
              About Us
            </a>
            <a
              href="/agents"
              className={styles.menuLink}
              onClick={(e) => {
                window.gtag?.("event", "select_content", {
                  content_type: "navigation",
                  content_id: "agents",
                  menu_type: "header",
                  destination: "/agents",
                  page_location: window.location.pathname,
                });
                handleMobileLink(e, "/agents");
              }}
            >
              Agents
            </a>
            <a
              href="/#locations"
              className={styles.menuLink}
              onClick={(e) => {
                window.gtag?.("event", "select_content", {
                  content_type: "navigation",
                  content_id: "locations",
                  menu_type: "header",
                  destination: "/#locations",
                  page_location: window.location.pathname,
                });
                handleMobileLink(e, "/#locations");
              }}
            >
              Locations
            </a>
            <a
              href="/faqs"
              className={styles.menuLink}
              onClick={(e) => {
                window.gtag?.("event", "select_content", {
                  content_type: "navigation",
                  content_id: "faqs",
                  menu_type: "header",
                  destination: "/faqs",
                  page_location: window.location.pathname,
                });
                handleMobileLink(e, "/faqs");
              }}
            >
              FAQs
            </a>
            <a
              href="/contact-us"
              className={styles.menuLink}
              onClick={(e) => {
                window.gtag?.("event", "select_content", {
                  content_type: "navigation",
                  content_id: "contact_us",
                  menu_type: "header",
                  destination: "/contact-us",
                  page_location: window.location.pathname,
                });
                handleMobileLink(e, "/contact-us");
              }}
            >
              Contact Us
            </a>
            <div className={styles.menuLink}></div>
            <div className={styles.menuLink}>
              <p className={styles.menuCredits}>
                © {siteName} {new Date().getFullYear()}. All Rights Reserved.
              </p>
            </div>
          </div>

          <button
            type="button"
            className={styles.menuBackdrop}
            aria-label="Close menu"
            onClick={() => {
              window.gtag?.("event", "select_content", {
                content_type: "navigation",
                content_id: "close_menu",
                menu_type: "header",
                destination: "mobile_menu",
                page_location: window.location.pathname,
              });
              setIsMenuOpen(false);
            }}
          />
        </div>
      ) : null}
    </header>
  );
}

export default React.memo(Header);
